import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminSidebar from '../../components/navigation/AdminSidebar';

import AnalyticsCard from './components/AnalyticsCard';
import CategoryManager from './components/CategoryManager';
import RestaurantProfileCard from './components/RestaurantProfileCard';
import DishStatsPanel from './components/DishStatsPanel';
import AddDishModal from './components/AddDishModal';
import PromotionsManager from './components/PromotionsManager';
import PaymentSection from './components/PaymentSection';
import MonthlyStatsModal from './components/MonthlyStatsModal';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import ErrorToast from '../../components/ui/ErrorToast';
import ChangePasswordModal from '../../components/ui/ChangePasswordModal';

import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import useEffectiveRestaurantId from '../../hooks/useEffectiveRestaurantId';
import { fetchTotalsWithComparison } from '../../services/dishStatsService';
import {
  fetchRestaurantProfile,
  updateRestaurantProfile,
  fetchCategories,
  addCategory,
  updateCategory,
  deleteCategory,
  fetchDishes,
  insertDish,
  deleteDish,
  upsertDish,
} from '../../services/menuDataService';
import { fetchPromotions } from '../../services/promotionsService';

// Helper to generate unique toast IDs
let _toastId = 0;
const newToastId = () => `toast-${++_toastId}`;

const RestaurantAdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const {
    isAuthenticated,
    signIn,
    signOut,
    canManageMenu,
    canManageStaff,
    isSuperAdmin,
    loading: authLoading,
    profileLoading,
    hasRestaurantAccess,
  } = useAuth();
  const restaurantId = useEffectiveRestaurantId();
  const restaurantIdParam = searchParams?.get('restaurant_id');

  // Keep a ref so async handlers always read the latest restaurantId (avoids stale closure)
  const restaurantIdRef = useRef(restaurantId);
  useEffect(() => {
    restaurantIdRef.current = restaurantId;
  }, [restaurantId]);

  // Super admins without a selected restaurant belong on the platform dashboard
  useEffect(() => {
    if (authLoading || profileLoading) return;
    if (isAuthenticated && isSuperAdmin && !restaurantIdParam && !restaurantId) {
      navigate('/super-admin', { replace: true });
    }
  }, [authLoading, profileLoading, isAuthenticated, isSuperAdmin, restaurantIdParam, restaurantId, navigate]);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAddDishModalOpen, setIsAddDishModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showChangePassword, setShowChangePassword] = useState(false);

  const [menuItems, setMenuItems] = useState([]);
  const [editorDishes, setEditorDishes] = useState(null);

  // Promotions state
  const [promotions, setPromotions] = useState([]);

  // Error toasts state
  const [errorToasts, setErrorToasts] = useState([]);
  const addToast = (message, type = 'error') =>
    setErrorToasts(prev => [...prev, { id: newToastId(), message, type }]);
  const dismissToast = (id) =>
    setErrorToasts(prev => prev?.filter(t => t?.id !== id));

  // Real-time analytics state
  const [totalAR, setTotalAR] = useState(null);
  const [totalQR, setTotalQR] = useState(null);
  const [popularDishCount, setPopularDishCount] = useState(null);
  const [arChange, setArChange] = useState(null);
  const [qrChange, setQrChange] = useState(null);
  const [popularChange, setPopularChange] = useState(null);

  const [activeTab, setActiveTab] = useState(() => {
    return searchParams?.get('tab') || 'dashboard';
  });

  const [monthlyStatsModal, setMonthlyStatsModal] = useState(null); // 'qr' | 'ar' | null

  const loadAnalytics = async () => {
    if (!restaurantId) return;
    try {
      const result = await fetchTotalsWithComparison(restaurantId);
      setTotalAR(result?.totalAR ?? 0);
      setTotalQR(result?.totalQR ?? 0);
      setPopularDishCount(result?.popular ?? 0);
      setArChange(result?.arChange);
      setQrChange(result?.qrChange);
      setPopularChange(result?.popularChange);
    } catch (err) {
      console.error('Analytics load error:', err?.message || err);
      setTotalAR(0);
      setTotalQR(0);
      setPopularDishCount(0);
    }
  };

  const loadPromotions = () => {
    if (!restaurantId) return;
    fetchPromotions(false, restaurantId)?.then(setPromotions)?.catch(() => addToast('Impossible de charger les promotions.', 'warning'));
  };

  const loadDishes = () => {
    if (!restaurantId) return;
    fetchDishes(restaurantId)
      ?.then(setMenuItems)
      ?.catch(() => addToast('Impossible de charger les plats.', 'warning'));
  };

  // ── Restaurant Profile (Supabase) ──────────────────────────────────────────
  const [restaurantProfile, setRestaurantProfile] = useState({
    name: '',
    tagline: '',
    cuisine: '',
    phone: '',
    email: '',
    address: '',
    hours: '',
    currency: 'CAD',
    logo: '',
    logoAlt: '',
    restaurantId: null,
  });

  // Keep restaurantId on the in-memory profile so saves/uploads never lose context
  useEffect(() => {
    if (!restaurantId) return;
    setRestaurantProfile((prev) => (
      prev?.restaurantId === restaurantId
        ? prev
        : { ...prev, restaurantId, restaurant_id: restaurantId }
    ));
  }, [restaurantId]);

  // ── Categories (Supabase) ──────────────────────────────────────────────────
  const [categories, setCategories] = useState([
    { id: 'appetizers', name: t('appetizers'), itemCount: 0 },
    { id: 'mains', name: t('mainCourse'), itemCount: 0 },
    { id: 'desserts', name: t('desserts'), itemCount: 0 },
    { id: 'beverages', name: t('beverages'), itemCount: 0 },
  ]);

  useEffect(() => {
    if (!restaurantId) return;

    loadAnalytics();
    loadPromotions();
    fetchRestaurantProfile(restaurantId)
      ?.then((profile) => {
        if (profile) {
          setRestaurantProfile({
            ...profile,
            restaurantId: profile?.restaurantId || restaurantId,
            restaurant_id: profile?.restaurantId || restaurantId,
          });
        }
      })
      ?.catch(() => addToast('Impossible de charger le profil du restaurant.', 'warning'));
    fetchCategories(restaurantId)
      ?.then(setCategories)
      ?.catch(() => addToast('Impossible de charger les catégories.', 'warning'));
    loadDishes();
  }, [restaurantId]);

  // ── Menu Dishes (Supabase) ─────────────────────────────────────────────────

  const analyticsData = [
  {
    title: t('totalQRScans'),
    value: totalQR !== null ? totalQR?.toLocaleString() : "—",
    change: qrChange?.value ?? null,
    changeType: qrChange?.type || "positive",
    icon: "QrCode",
    iconColor: "var(--color-primary)",
    subtitle: t('thisMonth'),
    onClick: () => setMonthlyStatsModal('qr'),
  },
  {
    title: t('arEngagements'),
    value: totalAR !== null ? totalAR?.toLocaleString() : "—",
    change: arChange?.value ?? null,
    changeType: arChange?.type || "positive",
    icon: "Scan",
    iconColor: "var(--color-accent)",
    subtitle: t('totalARViews'),
    onClick: () => setMonthlyStatsModal('ar'),
  },
  {
    title: t('popularDishes'),
    value: popularDishCount !== null ? String(popularDishCount) : "—",
    change: popularChange?.value ?? null,
    changeType: popularChange?.type || "positive",
    icon: "TrendingUp",
    iconColor: "var(--color-success)",
    subtitle: t('highEngagement')
  }];


  const handleAddDish = async (dishData) => {
    try {
      const dish = {
        ...dishData,
        price: parseFloat(dishData?.price),
        arUrl: dishData?.arModelUrl || '',
        isNew: false,
        isPopular: false,
        isVegetarian: false,
        spiceLevel: 0,
        hidden: dishData?.status === 'inactive' || dishData?.status === 'draft',
        sortOrder: menuItems?.length + 1,
        imageAlt: `Delicious ${dishData?.name} dish`,
      };
      const saved = await insertDish(dish, restaurantId);
      setMenuItems(prev => [...prev, saved]);
    } catch (e) {
      addToast('Échec de l\'ajout du plat.', 'error');
    }
    setIsAddDishModalOpen(false);
  };

  const handleEditDish = (dish) => {
    console.log('Edit dish:', dish);
  };

  const handleDeleteDish = async (dish) => {
    try {
      await deleteDish(dish?.id, restaurantId);
      setMenuItems(prev => prev?.filter((item) => item?.id !== dish?.id));
    } catch (e) {
      addToast('Échec de la suppression du plat.', 'error');
    }
  };

  const handleToggleStatus = async (dish) => {
    const updated = { ...dish, hidden: !dish?.hidden };
    try {
      await upsertDish(updated, restaurantId);
      setMenuItems(prev => prev?.map((item) => item?.id === dish?.id ? updated : item));
    } catch (e) {
      addToast('Échec de la mise à jour du statut.', 'error');
    }
  };

  const handleAddCategory = async (name) => {
    try {
      const newCat = await addCategory(name, restaurantId);
      setCategories(prev => [...prev, newCat]);
    } catch (e) {
      addToast('Échec de la sauvegarde de la catégorie.', 'error');
    }
  };

  const handleEditCategory = async (id, name) => {
    try {
      await updateCategory(id, name, restaurantId);
      setCategories(prev => prev?.map(cat => cat?.id === id ? { ...cat, name } : cat));
    } catch (e) {
      addToast('Échec de la mise à jour de la catégorie.', 'error');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await deleteCategory(id, restaurantId);
      setCategories(prev => prev?.filter(cat => cat?.id !== id));
    } catch (e) {
      addToast('Échec de la suppression de la catégorie.', 'error');
    }
  };

  const handleUpdateProfile = async (updatedProfile) => {
    const effectiveRestaurantId = restaurantIdRef?.current || restaurantId || updatedProfile?.restaurantId || updatedProfile?.restaurant_id;
    if (!effectiveRestaurantId) {
      console.error('Profile save error: no restaurant ID available', { restaurantIdRef: restaurantIdRef?.current, restaurantId, updatedProfile });
      addToast('Échec de la sauvegarde du profil du restaurant. Rechargez la page ou reconnectez-vous.', 'error');
      return;
    }
    const profileWithId = {
      ...updatedProfile,
      restaurantId: effectiveRestaurantId,
      restaurant_id: effectiveRestaurantId,
    };
    setRestaurantProfile(profileWithId);
    try {
      await updateRestaurantProfile(profileWithId, effectiveRestaurantId);
    } catch (e) {
      console.error('Profile save error:', e?.message || e);
      addToast('Échec de la sauvegarde du profil du restaurant.', 'error');
    }
  };

  const handleImportMenu = () => {
    console.log('Import menu functionality');
  };

  const handleExportData = () => {
    console.log('Export data functionality');
  };

  const withRestaurantId = (path) => {
    const id = restaurantIdRef?.current || restaurantId;
    if (!id) return path;
    const sep = path?.includes('?') ? '&' : '?';
    return `${path}${sep}restaurant_id=${id}`;
  };

  const handleGenerateQR = () => {
    navigate(withRestaurantId('/qr-code-generator'));
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    const { error } = await signIn(loginForm?.email, loginForm?.password);
    if (!error) {
      setLoginError('');
    } else {
      setLoginError(error?.message || 'Invalid email or password');
    }
  };

  const handleLogout = async () => {
    await signOut();
    setLoginForm({ email: '', password: '' });
  };

  const handleKiriModelReady = ({ url, serialNum }) => {
    // Pre-fill the AR model URL in the add dish modal
    setIsAddDishModalOpen(true);
    addToast(`Kiri Engine model ready (${serialNum}). Add it to a dish below.`, 'success');
  };

  const filteredMenuItems = menuItems?.filter((item) => {
    const matchesSearch = item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    item?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item?.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || item?.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const categoryOptions = [
  { value: 'all', label: t('allCategories') },
  ...categories?.map((cat) => ({ value: cat?.name, label: cat?.name }))];


  const statusOptions = [
  { value: 'all', label: t('allStatus') },
  { value: 'active', label: t('active') },
  { value: 'inactive', label: t('inactive') },
  { value: 'draft', label: t('draft') }];


  // Build preview items: prefer live editor dishes, fall back to menuItems
  const previewItems = editorDishes
    ? editorDishes
        ?.filter(d => !d?.hidden)
        ?.map(d => ({
          id: d?.id,
          name: d?.name,
          description: d?.description,
          price: d?.price,
          image: d?.image,
          imageAlt: d?.name,
          hasARModel: !!d?.arUrl,
          status: 'active',
        }))
    : menuItems?.filter(item => item?.status === 'active');

  if (authLoading || (isAuthenticated && profileLoading)) {
    return (
      <div className="restaurant-admin-dashboard min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="restaurant-admin-dashboard min-h-screen bg-background">
      {!isAuthenticated ?
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="w-full max-w-sm bg-card border border-border rounded-xl shadow-warm p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Lock" size={24} color="var(--color-primary)" />
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground">Admin Access</h1>
              <p className="text-sm text-muted-foreground mt-1">Sign in to manage your restaurant</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                <Input
                type="email"
                placeholder="owner@restaurant.com"
                value={loginForm?.email}
                onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e?.target?.value }))}
                required />
              
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Password</label>
                <Input
                type="password"
                placeholder="Enter password"
                value={loginForm?.password}
                onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e?.target?.value }))}
                required />
              
              </div>
              {loginError &&
            <p className="text-sm text-red-500 text-center">{loginError}</p>
            }
              <button
              type="submit"
              className="w-full py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors">
              
                Sign In
              </button>
              <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              
                ← Back to Menu
              </button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowChangePassword(true)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 mx-auto"
              >
                <Icon name="KeyRound" size={14} />
                Change Password
              </button>
            </div>
          </div>
          {showChangePassword && (
            <ChangePasswordModal
              onClose={() => setShowChangePassword(false)}
              resetMode={true}
              emailPlaceholder="owner@restaurant.com"
            />
          )}
        </div> :

      <>
      <AdminSidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          activeTab={activeTab}
          onTabChange={setActiveTab} />
        
      <div
          className={`dashboard-content transition-smooth ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`
          }>
          
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12 mt-16 lg:mt-0">
          <div className="dashboard-header mb-6 md:mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                  {activeTab === 'payment' ? 'Paiement' : 'Restaurant Dashboard'}
                </h1>
                <p className="text-sm md:text-base font-caption text-muted-foreground">
                  {activeTab === 'payment' ?'Gérez votre abonnement et vos paiements' :'Gérez votre menu numérique et suivez l\'engagement des clients'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors">
                  <Icon name="LogOut" size={16} />
                  Logout
                </button>
              </div>
            </div>
          </div>

          {!restaurantId ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center max-w-lg mx-auto">
              <div className="w-12 h-12 bg-warning/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={22} color="var(--color-warning)" />
              </div>
              <h2 className="text-lg font-heading font-semibold text-foreground mb-2">
                Restaurant non lié
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {isSuperAdmin
                  ? 'Ouvrez un restaurant depuis le tableau Super Admin (?restaurant_id=...) pour gérer son profil et son logo.'
                  : hasRestaurantAccess
                    ? 'Votre compte est connecté, mais aucun restaurant n\'a encore été résolu. Rechargez la page ou reconnectez-vous.' :'Votre compte n\'est lié à aucun restaurant. Contactez le support ou votre super admin.'}
              </p>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={() => navigate('/super-admin')}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Aller au Super Admin
                </button>
              )}
            </div>
          ) : activeTab === 'payment' ? (
            <PaymentSection restaurantId={restaurantId} />
          ) : (
            <>
              <div className="analytics-section mb-6 md:mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-muted-foreground font-caption">{t('thisMonth')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {analyticsData?.map((data, index) =>
                    <AnalyticsCard key={index} {...data} />
                    )}
                </div>
              </div>

              <div className="mb-6 md:mb-8">
                <PromotionsManager
                  promotions={promotions}
                  restaurantId={restaurantId}
                  onRefresh={loadPromotions}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 md:gap-8 mb-6 md:mb-8">
                <div className="space-y-6 md:space-y-8">
                  <CategoryManager
                      categories={categories}
                      onAddCategory={(isSuperAdmin || canManageMenu) ? handleAddCategory : undefined}
                      onEditCategory={(isSuperAdmin || canManageMenu) ? handleEditCategory : undefined}
                      onDeleteCategory={(isSuperAdmin || canManageMenu) ? handleDeleteCategory : undefined} />

                  <RestaurantProfileCard
                      profile={restaurantProfile}
                      restaurantId={restaurantId}
                      onUpdate={(isSuperAdmin || canManageMenu) ? handleUpdateProfile : () => {}}
                      onError={(message) => addToast(message, 'error')} />

                </div>
              </div>

              <DishStatsPanel />

              {!isSuperAdmin && !canManageStaff && (
                <p className="text-sm text-muted-foreground">
                  Staff access: you can view analytics and availability, but menu structure and staff settings are restricted.
                </p>
              )}
            </>
          )}
            
        </div>
      </div>
      <MonthlyStatsModal
        isOpen={monthlyStatsModal !== null}
        onClose={() => setMonthlyStatsModal(null)}
        type={monthlyStatsModal}
      />
      <AddDishModal
          isOpen={isAddDishModalOpen}
          onClose={() => setIsAddDishModalOpen(false)}
          onSave={handleAddDish}
          categories={categories}
          restaurantId={restaurantId} />

      {/* Error Toasts */}
      <ErrorToast toasts={errorToasts} onDismiss={dismissToast} />
        
        </>
      }
    </div>);

};

export default RestaurantAdminDashboard;